

import React, { useState } from 'react';
import {
  Layout,
  Form,
  Input,
  Button,
  notification,
  Divider,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Avatar,
  Spin,
  Result
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  HeartOutlined,
  SendOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { baseUrl, secretKey } from '../config';
import CryptoJS from 'crypto-js';

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const PurnimaForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const history = useHistory();

  // Mobile detection
  const isMobile = window.innerWidth <= 768;

  const onFinish = async (values) => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${baseUrl}/purnima_form/create`, values);
      
      if (response.status === 200) {
        setSubmittedData(values);
        setSubmitted(true);
        
        notification.success({
          message: 'सफलता!',
          description: 'जय ब्रह्माजी री सा। आपका पूर्णिमा फॉर्म सफलतापूर्वक भर दिया गया है।',
          placement: 'top',
          duration: 3,
        });
        
        form.resetFields();
        const id = response.data.data.user.id;
        const encryptedId = CryptoJS.AES.encrypt(id.toString(), secretKey).toString();
        const encodedId = encodeURIComponent(encryptedId);
        
        setTimeout(() => {
          history.push(`/purnima-member-confirm?id=${encodedId}`);
        }, 2000);
        
      } else {
        throw new Error(response.message || 'Form submission failed');
      }
    } catch (error) {
      notification.error({
        message: 'त्रुटि!',
        description: 'फॉर्म भरने में समस्या हुई है। कृपया पुनः प्रयास करें।',
        placement: 'top',
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    notification.error({
      message: 'फॉर्म में त्रुटि!',
      description: 'कृपया सभी आवश्यक फील्ड भरें।',
      placement: 'top',
    });
  };

  const resetForm = () => {
    setSubmitted(false);
    setSubmittedData(null);
    form.resetFields();
  };

  // Mobile-optimized styles
  const mobileStyles = {
    headerStyle: {
      background: 'rgba(255, 107, 53, 0.95)',
      textAlign: 'center',
      padding: isMobile ? '15px 10px' : '20px',
      height: "auto",
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    contentStyle: {
      padding: isMobile ? '15px 10px' : '30px 20px',
      minHeight: '80vh'
    },
    cardStyle: {
      borderRadius: '12px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      background: 'rgba(255, 255, 255, 0.95)',
      padding: isMobile ? '16px' : '24px'
    },
    headerCardStyle: {
      marginBottom: isMobile ? '16px' : '24px',
      borderRadius: '12px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      textAlign: 'center',
      background: 'rgba(255, 255, 255, 0.95)',
      padding: isMobile ? '16px' : '24px'
    },
    inputStyle: {
      height: isMobile ? '48px' : '40px',
      fontSize: isMobile ? '16px' : '14px',
      borderRadius: '8px'
    },
    buttonStyle: {
      height: isMobile ? '56px' : '50px',
      fontSize: isMobile ? '18px' : '16px',
      fontWeight: '600',
      minWidth: isMobile ? '100%' : '200px'
    }
  };

  // Success Screen
  if (submitted) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
        padding: isMobile ? '10px' : '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card style={{
          maxWidth: isMobile ? '100%' : '600px',
          width: '100%',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <Result
            status="success"
            title={
              <Title level={isMobile ? 3 : 2} style={{ 
                color: '#52c41a', 
                marginBottom: '16px',
                fontSize: isMobile ? '20px' : '24px'
              }}>
                जय गुरु महाराज री सा!
              </Title>
            }
            subTitle={
              <div>
                <Paragraph style={{ 
                  fontSize: isMobile ? '14px' : '18px', 
                  marginBottom: '20px', 
                  color: '#333' 
                }}>
                  धन्यवाद <Text strong style={{ color: '#ff6b35' }}>{submittedData?.name}</Text> जी।
                  आपका पूर्णिमा फॉर्म सफलतापूर्वक भर दिया गया है।
                </Paragraph>
                <Paragraph style={{ 
                  fontSize: isMobile ? '13px' : '16px', 
                  color: '#666' 
                }}>
                  आगामी पूर्णिमा की जानकारी आपको पूर्व में मिल जाएगी।
                </Paragraph>
              </div>
            }
            extra={[
              <Button 
                type="primary" 
                size="large"
                onClick={resetForm}
                style={{
                  ...mobileStyles.buttonStyle,
                  background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
                  border: 'none',
                  borderRadius: '8px'
                }}
                block={isMobile}
              >
                एक और फॉर्म भरें
              </Button>
            ]}
          />

          {submittedData && (
            <Card 
              size="small" 
              style={{ 
                background: '#fff7e6', 
                border: '1px solid #ffd591',
                borderRadius: '8px',
                marginTop: '20px'
              }}
              title={
                <Text strong style={{ 
                  color: '#ff6b35',
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  आपकी जानकारी
                </Text>
              }
            >
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    नाम:
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                    {submittedData.name}
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    सम्पर्क सूत्र:
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                    {submittedData.phone_no}
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    गाँव:
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                    {submittedData.village}
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    जिला:
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                    {submittedData.district}
                  </Text>
                </Col>
              </Row>
            </Card>
          )}
        </Card>
      </div>
    );
  }

  return (
    <Layout style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)'
    }}>
      {/* Header */}
      <Header style={mobileStyles.headerStyle}>
        <Space direction="vertical" size={isMobile ? "small" : "middle"} style={{ width: '100%' }}>
          <Title level={isMobile ? 2 : 1} style={{ 
            color: '#fff', 
            fontSize: isMobile ? '22px' : '32px', 
            fontWeight: 'bold', 
            margin: 0,
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
          }}>
            जय गुरु महाराज री सा
          </Title>
          
          <Paragraph style={{ 
            color: '#fff', 
            fontWeight: '600', 
            fontSize: isMobile ? '13px' : '16px', 
            margin: 0,
            lineHeight: '1.6',
            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
            padding: isMobile ? '0 5px' : '0'
          }}>
            श्री खेतेश्वर ब्रह्मधाम तीर्थ, आसोतरा में पूर्णिमा पर्व पर पधारने वाले बंधु/भगिनी अपनी जानकारी इस पूर्णिमा फॉर्म में अवश्य भरे।
          </Paragraph>
          
          <Title level={isMobile ? 4 : 3} style={{ 
            color: '#fff', 
            fontWeight: 'bold', 
            fontSize: isMobile ? '14px' : '18px', 
            lineHeight: '1.5',
            margin: 0,
            textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
          }}>
            अखिल भारतीय राजपुरोहित समाज विकास संस्थान <br /> 
            श्री खेतेश्वर ब्रह्मधाम तीर्थ, आसोतरा
          </Title>
        </Space>
      </Header>

      <Divider style={{ margin: 0 }} />

      {/* Content */}
      <Content style={mobileStyles.contentStyle}>
        <Row justify="center">
          <Col xs={24} sm={20} md={16} lg={12} xl={10}>
            {/* Header Card */}
            <Card style={mobileStyles.headerCardStyle}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Avatar
                  size={isMobile ? 50 : 70}
                  style={{
                    background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
                    color: 'white'
                  }}
                  icon={<HeartOutlined style={{ fontSize: isMobile ? '20px' : '32px' }} />}
                />
                
                <div>
                  <Title level={isMobile ? 3 : 2} style={{ 
                    margin: 0, 
                    color: '#ff6b35',
                    fontSize: isMobile ? '20px' : '24px'
                  }}>
                    पूर्णिमा फॉर्म
                  </Title>
                  <Text style={{ 
                    fontSize: isMobile ? '12px' : '14px', 
                    color: '#666' 
                  }}>
                    कृपया अपनी जानकारी भरें
                  </Text>
                </div>
              </Space>
            </Card>

            {/* Form Card */}
            <Card
              style={mobileStyles.cardStyle}
              title={
                <Space>
                  <UserOutlined style={{ color: '#ff6b35' }} />
                  <Text strong style={{ 
                    color: '#ff6b35',
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    व्यक्तिगत जानकारी
                  </Text>
                </Space>
              }
            >
              <Spin spinning={loading} tip="फॉर्म जमा हो रहा है...">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  requiredMark={false}
                  size="large"
                >
                  {/* All fields in single column on mobile */}
                  <Row gutter={isMobile ? [0, 12] : [16, 8]}>
                    {/* सम्पर्क सूत्र / Phone No */}
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                          सम्पर्क सूत्र *
                        </Text>}
                        name="phone_no"
                        rules={[
                          { required: true, message: 'कृपया सम्पर्क सूत्र दर्ज करें!' },
                          { pattern: /^[0-9]{10}$/, message: 'कृपया मान्य मोबाइल नंबर दर्ज करें!' }
                        ]}
                      >
                        <Input 
                          prefix={<PhoneOutlined style={{ color: '#ff6b35' }} />}
                          placeholder="फोन नंबर दर्ज करें" 
                          style={mobileStyles.inputStyle}
                          maxLength={10}
                        />
                      </Form.Item>
                    </Col>

                    {/* नाम / Name */}
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                          नाम *
                        </Text>}
                        name="name"
                        rules={[{ required: true, message: 'कृपया नाम दर्ज करें!' }]}
                      >
                        <Input 
                          prefix={<UserOutlined style={{ color: '#ff6b35' }} />}
                          placeholder="अपना नाम दर्ज करें" 
                          style={mobileStyles.inputStyle}
                        />
                      </Form.Item>
                    </Col>

                    {/* पिता का नाम / Father's Name */}
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                          पिता का नाम *
                        </Text>}
                        name="father_name"
                        rules={[{ required: true, message: 'कृपया पिता का नाम दर्ज करें!' }]}
                      >
                        <Input 
                          prefix={<UserOutlined style={{ color: '#ff6b35' }} />}
                          placeholder="पिता का नाम दर्ज करें" 
                          style={mobileStyles.inputStyle}
                        />
                      </Form.Item>
                    </Col>

                    {/* गाँव / Village */}
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                          गाँव *
                        </Text>}
                        name="village"
                        rules={[{ required: true, message: 'कृपया गाँव का नाम दर्ज करें!' }]}
                      >
                        <Input 
                          prefix={<HomeOutlined style={{ color: '#ff6b35' }} />}
                          placeholder="गाँव का नाम दर्ज करें" 
                          style={mobileStyles.inputStyle}
                        />
                      </Form.Item>
                    </Col>

                    {/* तहसील / Tehsil */}
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                          तहसील *
                        </Text>}
                        name="tehsil"
                        rules={[{ required: true, message: 'कृपया तहसील दर्ज करें!' }]}
                      >
                        <Input 
                          prefix={<EnvironmentOutlined style={{ color: '#ff6b35' }} />}
                          placeholder="तहसील दर्ज करें" 
                          style={mobileStyles.inputStyle}
                        />
                      </Form.Item>
                    </Col>

                    {/* जिला / District */}
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                          जिला *
                        </Text>}
                        name="district"
                        rules={[{ required: true, message: 'कृपया जिला दर्ज करें!' }]}
                      >
                        <Input 
                          prefix={<EnvironmentOutlined style={{ color: '#ff6b35' }} />}
                          placeholder="जिला दर्ज करें" 
                          style={mobileStyles.inputStyle}
                        />
                      </Form.Item>
                    </Col>

                    {/* व्यवसाय / Occupation */}
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                          व्यवसाय *
                        </Text>}
                        name="occupation"
                        rules={[{ required: true, message: 'कृपया व्यवसाय दर्ज करें!' }]}
                      >
                        <Input 
                          prefix={<ShopOutlined style={{ color: '#ff6b35' }} />}
                          placeholder="व्यवसाय दर्ज करें" 
                          style={mobileStyles.inputStyle}
                        />
                      </Form.Item>
                    </Col>

                    {/* व्यवसाय स्थान / Occupation District */}
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                          व्यवसाय स्थान *
                        </Text>}
                        name="occ_district"
                        rules={[{ required: true, message: 'कृपया व्यवसाय स्थान दर्ज करें!' }]}
                      >
                        <Input 
                          prefix={<EnvironmentOutlined style={{ color: '#ff6b35' }} />}
                          placeholder="व्यवसाय स्थान दर्ज करें" 
                          style={mobileStyles.inputStyle}
                        />
                      </Form.Item>
                    </Col>

                    {/* प्रेरणा सोत्र / Inspiration Remark */}
                    <Col xs={24}>
                      <Form.Item 
                        label={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                          प्रेरणा सोत्र
                        </Text>} 
                        name="inspiration_remark"
                      >
                        <TextArea 
                          placeholder="प्रेरणा सोत्र दर्ज करें" 
                          rows={isMobile ? 3 : 3}
                          style={{
                            borderRadius: '8px',
                            fontSize: isMobile ? '16px' : '14px'
                          }}
                          maxLength={300}
                          showCount
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {!isMobile && <Divider />}

                  {/* Submit Section */}
                  <div style={{ 
                    textAlign: 'center',
                    marginTop: isMobile ? '20px' : '32px'
                  }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        loading={loading}
                        icon={<SendOutlined />}
                        style={{
                          ...mobileStyles.buttonStyle,
                          background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
                          border: 'none',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)'
                        }}
                        block={isMobile}
                      >
                        {loading ? 'जमा हो रहा है...' : 'फॉर्म सबमिट करें'}
                      </Button>
                      
                      <Card 
                        size="small" 
                        style={{ 
                          background: '#fff7e6', 
                          border: '1px solid #ffd591',
                          borderRadius: '8px',
                          margin: '0 auto'
                        }}
                      >
                        <Text style={{ 
                          color: '#666', 
                          fontSize: isMobile ? '12px' : '14px' 
                        }}>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                          आपकी जानकारी सुरक्षित है और केवल पूर्णिमा व्यवस्था के लिए उपयोग होगी।
                        </Text>
                      </Card>
                    </Space>
                  </div>
                </Form>
              </Spin>
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default PurnimaForm;